import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { toast } from "sonner";
import { Save, Loader2, Gift } from "lucide-react";

const NATIONALITIES_FALLBACK = [
  "Expat Arab",
  "Local",
  "Asian",
  "European",
  "Russian",
  "South East Asian",
  "American",
  "African",
];
const CURRENT_PRODUCTS_FALLBACK = [
  "Cigarettes",
  "Vape",
  "Iqos",
  "Velo",
  "Zyn",
  "UBBS",
  "Nicotine Pouches - Others",
];
const HEARD_FALLBACK = ["yes", "no"];
const UBBS_PRODUCTS_FALLBACK = [
  "None",
  "Peppermint 6mg",
  "Salted Caramel 6mg",
  "Menthol 11mg",
  "Spearmint 11mg",
  "Berry 11mg",
  "Watermelon 11mg",
  "Menthol 14mg",
  "Peppermint 14mg",
  "Peppermint 20mg",
];
const NIC_LEVELS_FALLBACK = [
  "Less than 3mg",
  "4mg to 6mg",
  "7mg to 10mg",
  "11mg to 14mg",
  "15mg to 17mg",
  "18mg to 20mg",
];

const blank = {
  customer_nationality: "",
  heard_about_ubbs: "",
  current_product: "",
  ubbs_products_bought: [],
  ubbs_products_qty: {},
  nicotine_level: "",
  flavor: "",
  feedback: "",
  giveaway_cases: 0,
};

function computeEligibility(g, form) {
  if (!g || !g.applicable) return { eligible: false, reason: null, max: 0 };
  const total = (form.ubbs_products_bought || []).reduce((acc, p) => {
    if (p === "None") return acc;
    return acc + (parseInt(form.ubbs_products_qty[p], 10) || 1);
  }, 0);
  if (total <= 0) return { eligible: false, reason: null, max: 0 };
  if (
    g.competitor_enabled &&
    (g.competitor_brands || []).includes(form.current_product) &&
    total >= Math.max(1, g.competitor_min || 1)
  ) {
    return { eligible: true, reason: "competitor_switcher", max: Math.max(1, g.competitor_cases || 1) };
  }
  const pmin = g.purchase_min || 0;
  const cpr = Math.max(1, g.cases_per_reward || 1);
  if (pmin > 0 && total >= pmin) {
    const max = g.allow_multiple ? Math.floor(total / pmin) * cpr : cpr;
    return { eligible: true, reason: "standard", max: Math.max(1, max) };
  }
  return { eligible: false, reason: null, max: 0 };
}

const REASON_LABEL = {
  standard: "Standard giveaway",
  competitor_switcher: "Competitor switcher",
};

export default function PromoterSurvey() {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(true);
  const [ubbsProducts, setUbbsProducts] = useState(UBBS_PRODUCTS_FALLBACK);
  const [nationalities, setNationalities] = useState(NATIONALITIES_FALLBACK);
  const [currentProducts, setCurrentProducts] = useState(CURRENT_PRODUCTS_FALLBACK);
  const [heardOptions, setHeardOptions] = useState(HEARD_FALLBACK);
  const [nicLevels, setNicLevels] = useState(NIC_LEVELS_FALLBACK);
  const [skuList, setSkuList] = useState([]);
  const [stockQuant, setStockQuant] = useState(null);
  const [giveaway, setGiveaway] = useState(null);

  const loadGiveaway = () => {
    api
      .get("/promoter/giveaway")
      .then((r) => setGiveaway(r.data || { applicable: false }))
      .catch(() => {});
  };

  const loadStock = () => {
    api
      .get("/promoter/stock/current")
      .then((r) => setStockQuant((r.data && r.data.quantities) || null))
      .catch(() => {});
  };

  const availByName = useMemo(() => {
    if (!stockQuant) return null;
    const m = {};
    skuList.forEach((s) => {
      m[s.name] = parseInt(stockQuant[s.id], 10) || 0;
    });
    return m;
  }, [skuList, stockQuant]);

  const availFor = (p) =>
    availByName && p in availByName ? availByName[p] : Infinity;

  useEffect(() => {
    loadStock();
    loadGiveaway();
    api
      .get("/promoter/me")
      .then((r) => setActive(!!r.data.current_shift))
      .catch(() => {});
    // Load SKU master list so the survey "UBBS products bought" stays in sync
    // with the SKU list managed by the admin (admin → SKUs).
    api
      .get("/promoter/skus")
      .then((r) => {
        setSkuList(r.data || []);
        const names = (r.data || [])
          .filter((s) => (s.category || "").toUpperCase() === "UBBS")
          .map((s) => s.name);
        if (names.length) setUbbsProducts(["None", ...names]);
      })
      .catch(() => {});
    // Load admin-editable survey dropdown options
    api
      .get("/promoter/survey-options")
      .then((r) => {
        const d = r.data || {};
        if (Array.isArray(d.nationalities) && d.nationalities.length) setNationalities(d.nationalities);
        if (Array.isArray(d.current_products) && d.current_products.length) setCurrentProducts(d.current_products);
        if (Array.isArray(d.heard_about_ubbs) && d.heard_about_ubbs.length) setHeardOptions(d.heard_about_ubbs);
        if (Array.isArray(d.nicotine_levels) && d.nicotine_levels.length) setNicLevels(d.nicotine_levels);
      })
      .catch(() => {});
  }, []);

  const toggleProduct = (p) => {
    const exists = form.ubbs_products_bought.includes(p);
    if (p === "None") {
      setForm({ ...form, ubbs_products_bought: exists ? [] : ["None"], ubbs_products_qty: {} });
      return;
    }
    if (!exists && availFor(p) <= 0) {
      toast.error(`${p} is out of stock`);
      return;
    }
    const without = form.ubbs_products_bought.filter((x) => x !== "None");
    const qty = { ...form.ubbs_products_qty };
    if (exists) {
      delete qty[p];
      setForm({
        ...form,
        ubbs_products_bought: without.filter((x) => x !== p),
        ubbs_products_qty: qty,
      });
    } else {
      qty[p] = 1;
      setForm({ ...form, ubbs_products_bought: [...without, p], ubbs_products_qty: qty });
    }
  };

  const changeQty = (p, delta) => {
    const cur = parseInt(form.ubbs_products_qty[p], 10) || 1;
    let next = Math.max(1, cur + delta);
    const avail = availFor(p);
    if (delta > 0 && next > avail) {
      toast.error(`Only ${avail} left in stock for ${p}`);
      next = Math.max(1, Math.min(next, avail));
    }
    setForm({ ...form, ubbs_products_qty: { ...form.ubbs_products_qty, [p]: next } });
  };

  const elig = useMemo(() => computeEligibility(giveaway, form), [giveaway, form]);
  const casesRemaining = giveaway?.cases_remaining ?? 0;
  const maxCases = Math.min(elig.max, casesRemaining);

  // Auto-default the giveaway quantity to 1 when a customer becomes eligible,
  // and reset to 0 when they are not (keeps the flow effortless for promoters).
  useEffect(() => {
    if (elig.eligible && casesRemaining > 0) {
      setForm((f) => {
        const want = f.giveaway_cases > 0 ? Math.min(f.giveaway_cases, maxCases) : 1;
        return f.giveaway_cases === want ? f : { ...f, giveaway_cases: want };
      });
    } else {
      setForm((f) => (f.giveaway_cases ? { ...f, giveaway_cases: 0 } : f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elig.eligible, maxCases, casesRemaining]);

  const changeCases = (delta) => {
    setForm((f) => {
      const next = Math.max(0, Math.min((f.giveaway_cases || 0) + delta, maxCases));
      return { ...f, giveaway_cases: next };
    });
  };

  const submit = async () => {
    if (!form.customer_nationality) return toast.error("Select nationality");
    if (!form.heard_about_ubbs) return toast.error("Select heard about UBBS");
    if (!form.current_product) return toast.error("Select current product");
    if (!form.ubbs_products_bought.length)
      return toast.error("Select at least one UBBS product (or 'None')");
    if (!form.nicotine_level) return toast.error("Select nicotine level");
    if (!form.flavor.trim()) return toast.error("Preferred flavor is required");
    setBusy(true);
    try {
      await api.post("/promoter/survey", form);
      toast.success(form.giveaway_cases > 0 ? `Survey saved · ${form.giveaway_cases} case(s) given` : "Survey saved");
      setForm(blank);
      loadStock();
      loadGiveaway();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!active) {
    return (
      <div className="glass-panel p-6 text-center text-sm text-neutral-500">
        Check in first to log customer surveys.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          New customer interaction
        </div>
        <h1 className="font-display font-semibold text-3xl tracking-tight">Survey</h1>
      </div>

      {giveaway?.applicable && (
        <div className="glass-panel p-4 border-emerald-200/70 bg-emerald-50/40" data-testid="giveaway-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <Gift className="h-4 w-4 text-emerald-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-emerald-800">Silicone Case Giveaway</div>
              <div className="text-xs text-emerald-700/90 mt-0.5">
                {giveaway.banner ||
                  (giveaway.purchase_min > 0
                    ? `Buy ${giveaway.purchase_min} Übbs cans → ${giveaway.cases_per_reward} free silicone case(s).`
                    : "Reward eligible customers with a silicone case.")}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-widest font-mono">
                <span className="rounded-full bg-white/70 border border-emerald-200/70 px-2 py-0.5 text-emerald-700" data-testid="giveaway-remaining">
                  {giveaway.cases_remaining} cases left
                </span>
                {giveaway.session_target > 0 && (
                  <span className="rounded-full bg-white/70 border border-emerald-200/70 px-2 py-0.5 text-emerald-700">
                    Session target {giveaway.session_target}
                  </span>
                )}
                {giveaway.competitor_enabled && (
                  <span className="rounded-full bg-white/70 border border-emerald-200/70 px-2 py-0.5 text-emerald-700">
                    Switcher: {giveaway.competitor_cases} case(s)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Customer Nationality *</Label>
          <Select value={form.customer_nationality} onValueChange={(v) => setForm({ ...form, customer_nationality: v })}>
            <SelectTrigger className="rounded-xl mt-2" data-testid="survey-nationality">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {nationalities.map((n) => (
                <SelectItem key={n} value={n}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Heard about UBBS? *</Label>
          <RadioGroup
            value={form.heard_about_ubbs}
            onValueChange={(v) => setForm({ ...form, heard_about_ubbs: v })}
            className="flex flex-wrap gap-3 mt-2"
            data-testid="survey-heard"
          >
            {heardOptions.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 border border-slate-300/70 px-3 py-2 cursor-pointer"
              >
                <RadioGroupItem value={opt} id={`heard-${opt}`} />
                <span className="text-sm capitalize">{opt}</span>
              </label>
            ))}
          </RadioGroup>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Current Product Used *</Label>
          <Select value={form.current_product} onValueChange={(v) => setForm({ ...form, current_product: v })}>
            <SelectTrigger className="rounded-xl mt-2" data-testid="survey-current-product">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {currentProducts.map((n) => (
                <SelectItem key={n} value={n}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">UBBS Products Bought *</Label>
          <div className="glass-panel mt-2 divide-y divide-slate-200/50" data-testid="survey-ubbs-products">
            {ubbsProducts.map((p) => {
              const checked = form.ubbs_products_bought.includes(p);
              return (
                <div key={p} className="flex items-center gap-3 p-3">
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleProduct(p)}
                    className="rounded-xl"
                    data-testid={`survey-product-${p}`}
                  />
                  <span
                    className={`text-sm flex-1 cursor-pointer ${p === "None" ? "italic text-neutral-500" : ""}`}
                    onClick={() => toggleProduct(p)}
                  >
                    {p}
                    {p !== "None" && availByName && (
                      <span className="ml-2 font-mono text-[10px] text-neutral-400">
                        {availFor(p)} in stock
                      </span>
                    )}
                  </span>
                  {checked && p !== "None" && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => changeQty(p, -1)}
                        className="w-8 h-8 border border-slate-300/70 flex items-center justify-center text-lg leading-none hover:bg-white/80"
                        data-testid={`survey-qty-minus-${p}`}
                      >
                        −
                      </button>
                      <span
                        className="w-9 text-center font-mono font-semibold"
                        data-testid={`survey-qty-${p}`}
                      >
                        {form.ubbs_products_qty[p] || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => changeQty(p, 1)}
                        className="w-8 h-8 border border-slate-300/70 flex items-center justify-center text-lg leading-none hover:bg-white/80"
                        data-testid={`survey-qty-plus-${p}`}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Preferred Nicotine Level *</Label>
          <Select value={form.nicotine_level} onValueChange={(v) => setForm({ ...form, nicotine_level: v })}>
            <SelectTrigger className="rounded-xl mt-2" data-testid="survey-nicotine">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {nicLevels.map((n) => (
                <SelectItem key={n} value={n}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Preferred Flavor *</Label>
          <Input
            className="rounded-xl mt-2"
            value={form.flavor}
            onChange={(e) => setForm({ ...form, flavor: e.target.value })}
            data-testid="survey-flavor"
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Feedback (optional)</Label>
          <Textarea
            className="rounded-xl mt-2 min-h-[100px]"
            value={form.feedback}
            onChange={(e) => setForm({ ...form, feedback: e.target.value })}
            data-testid="survey-feedback"
          />
        </div>

        {giveaway?.applicable && elig.eligible && (
          <div
            className="rounded-2xl border-2 border-emerald-300/80 bg-emerald-50/60 p-4 fade-up"
            data-testid="giveaway-eligible-card"
          >
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-emerald-700" />
              <span className="font-semibold text-sm text-emerald-800">
                Eligible — give silicone case
              </span>
              <span className="ml-auto text-[10px] uppercase tracking-widest font-mono text-emerald-700 bg-white/70 border border-emerald-200/70 rounded-full px-2 py-0.5" data-testid="giveaway-reason">
                {REASON_LABEL[elig.reason]}
              </span>
            </div>
            {casesRemaining <= 0 ? (
              <div className="mt-3 text-sm text-red-600" data-testid="giveaway-no-stock">
                No silicone cases left in this store's inventory.
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-between">
                <div className="text-sm text-emerald-800">
                  Silicone Cases Given
                  <span className="block text-[11px] text-emerald-600/80">
                    Max {maxCases} · {casesRemaining} in stock
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => changeCases(-1)}
                    className="w-10 h-10 rounded-full border border-emerald-300/70 bg-white/80 flex items-center justify-center text-xl leading-none hover:bg-white"
                    data-testid="giveaway-cases-minus"
                  >
                    −
                  </button>
                  <span
                    className="w-12 text-center font-display font-bold text-2xl text-emerald-800"
                    data-testid="giveaway-cases-value"
                  >
                    {form.giveaway_cases}
                  </span>
                  <button
                    type="button"
                    onClick={() => changeCases(1)}
                    disabled={form.giveaway_cases >= maxCases}
                    className="w-10 h-10 rounded-full border border-emerald-300/70 bg-white/80 flex items-center justify-center text-xl leading-none hover:bg-white disabled:opacity-40"
                    data-testid="giveaway-cases-plus"
                  >
                    +
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={submit}
          disabled={busy}
          className="rounded-xl w-full h-14 bg-slate-900 hover:bg-slate-700 text-white uppercase tracking-wider font-semibold"
          data-testid="save-survey-button"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Save className="h-5 w-5 mr-2" /> Save survey</>)}
        </Button>
      </div>
    </div>
  );
}
